import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../redux-store/reducers';
import { featureToggles } from '../redux-store/actions';
import { togglerType } from '../redux-store/types/mdms';
import { mdmsProp } from '../redux-store/reducers/mdmsData';

export const useFeatureToggles = (): mdmsProp => {
  const dispatch = useDispatch();
  const mdmsData = useSelector((state: RootStateType) => state.mdmsDataReducer);
  const [toggler, setMdmsData] = useState<togglerType>(mdmsData?.toggler);

  useEffect(() => {
    const fetchToggles = async () => {
      try {
        const inbound = mdmsData?.toggler?.fatcaMasterInboundToggle ?? {};
        const outbound = mdmsData?.toggler?.fatcaMasterOutboundToggle ?? {};
        const nfoDates = mdmsData?.toggler?.nfoDates;

        // Decide if you need to fetch — e.g., if any value is missing
        const shouldFetch =
          Object.values(inbound).some((val) => !val) ||
          Object.values(outbound).some((val) => !val) ||
          nfoDates.some((date) => !date.startDate);

        if (shouldFetch) {
          const mdmsData = await dispatch(featureToggles());
          setMdmsData(mdmsData as unknown as togglerType);
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    };

    fetchToggles();
  }, [dispatch]);

  return { ...mdmsData, toggler };
};

export const useMdmsFatcaData = (fundFlowType: string): string => {
  const dispatch = useDispatch();
  const { application } = useSelector((state: RootStateType) => state.application);
  const [fatcaToFetch, setFatcaToFetch] = useState<string>('');

  useEffect(() => {
    const fetchFatcaData = async () => {
      try {
        if (Object.keys(application || {}).length !== 0) {
          // Dispatch to get toggles
          const { fatcaMasterInboundToggle, fatcaMasterOutboundToggle } = (await dispatch(
            featureToggles()
          )) as unknown as togglerType;

          // Select toggle set based on fundFlowType
          const toggleSet =
            fundFlowType === 'inbound'
              ? fatcaMasterInboundToggle ?? {}
              : fatcaMasterOutboundToggle ?? {};

          // Sort dates descending
          const dates: string[] = Object.keys(toggleSet).sort(
            (a, b) => new Date(b).getTime() - new Date(a).getTime()
          );

          const givenDate = new Date(application?.createdAt || '');

          // Find the first date less than the given date
          const firstDateLessThanGiven = dates.find(
            (date) => new Date(date).getTime() < givenDate.getTime()
          );

          // Pick the file corresponding to that date
          const fatcaFile: string = toggleSet[firstDateLessThanGiven || ''] || '';

          setFatcaToFetch(fatcaFile);
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    };

    fetchFatcaData();
  }, [dispatch, application, fundFlowType]);

  return fatcaToFetch;
};
